import Hero from "~/components/hero";
import { serverCallAPI, serverCallApiCMS } from "~/services/serverCallAPI";
import queryString from "query-string";
import { searchProduct } from "~/utils";
import type { Route } from "./+types/_index";
import SectionChild from "~/components/SectionChild";
import BreadcrumbPageCus from "~/components/breadcrumb-page";
import { CardThumbnail } from "~/components/card";
import Pagination from "~/components/pagination";
import FormSearchProduct from "../__components/form-search-product";
import { useLoaderData, useSearchParams } from "react-router";
import FormContact from "../__components/form-contact";
import ContentPage from "../../__components/content-page";
import { IPage } from "~/types/types";
import { useDataDropdown } from "~/utils/home";

type ProjectThumbnail = {
  fileName?: string | null;
  fileUrl?: string | null;
  mimeType?: string | null;
  extensions?: string | null;
};

type ProjectItem = {
  id: string;
  name: string;
  thumbnailUrls?: string[] | null;
  thumbnails?: ProjectThumbnail[] | null;
  description?: string | null;
  address?: string | null;
  slug: string;
};

type ProjectSearchResponse = {
  data: ProjectItem[];
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
};

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const searchParams = url.searchParams;
  const pageNumber = Number(searchParams.get("pageNumber")) || 1;
  const pageSize = Number(searchParams.get("pageSize")) || 9;
  const params = searchProduct({
    areaRange: searchParams.get("areaRange"),
    categoryId: searchParams.get("categoryId"),
    priceRange: searchParams.get("priceRange"),
    provinceId: searchParams.get("provinceId"),
    search: searchParams.get("search"),
  });
  const stringified = queryString.stringify(
    {
      ...params,
      pageNumber,
      pageSize,
    },
    { skipEmptyString: true, skipNull: true }
  );

  const rawResponse = await serverCallAPI<any>(
    "/api/project/public" + (stringified ? `?${stringified}` : "")
  ).catch(() => null);

  const fallbackProject: ProjectSearchResponse = {
    data: [],
    currentPage: pageNumber,
    totalPages: 1,
    pageSize,
    totalCount: 0,
  };

  const normalizeProjectResponse = (
    input: any,
    fallback: ProjectSearchResponse
  ): ProjectSearchResponse => {
    if (!input) return fallback;
    if (input?.data?.data && Array.isArray(input.data.data)) {
      return {
        data: input.data.data,
        currentPage: Number(input.data.currentPage) || fallback.currentPage,
        totalPages: Number(input.data.totalPages) || fallback.totalPages,
        pageSize: Number(input.data.pageSize) || fallback.pageSize,
        totalCount: Number(input.data.totalCount) || input.data.data.length,
      };
    }
    if (input?.data && Array.isArray(input.data)) {
      return {
        ...fallback,
        data: input.data,
        totalCount: input.data.length,
      };
    }
    if (input?.data && Array.isArray(input.data?.data)) {
      return {
        data: input.data.data,
        currentPage: Number(input.data.currentPage) || fallback.currentPage,
        totalPages: Number(input.data.totalPages) || fallback.totalPages,
        pageSize: Number(input.data.pageSize) || fallback.pageSize,
        totalCount: Number(input.data.totalCount) || input.data.data.length,
      };
    }
    if (Array.isArray(input)) {
      return {
        ...fallback,
        data: input,
        totalCount: input.length,
      };
    }
    return fallback;
  };

  const dataProject = normalizeProjectResponse(rawResponse, fallbackProject);

  const contentPage = await serverCallApiCMS<IPage>(
    "/api/v1/web/pages/mua-ban-mua-ban-can-ho"
  ).catch(() => {
    return {
      slug: "",
      description: "",
      content: "",
      seo: {
        title: "",
        meta_description: "",
        focus_keyphrase: "",
        og_image: [],
      },
      structured: null,
      structuredData: null,
    };
  });

  return {
    dataProject,
    params,
    page: {
      pageNumber,
      pageSize,
    },
    contentPage,
  };
}

function ListProject({ projects }: { projects: ProjectItem[] }) {
  if (!projects?.length) {
    return (
      <div className="py-8 text-center">
        Hiện không có dự án nào phù hợp với từ khóa đã chọn.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 pb-16 md:grid-cols-3">
      {projects.map((project) => {
        const imageUrls =
          project.thumbnailUrls?.length
            ? project.thumbnailUrls
            : project.thumbnails
                ?.map((thumb) => thumb.fileUrl || "")
                .filter(Boolean);
        const description = project.description || project.address || "";

        return (
          <div
            className="w-full md:max-w-[240px]
                md:h-[340px]
                lg:max-w-full lg:min-h-[520px] lg:h-auto"
            key={project.id}
          >
            <CardThumbnail
              title={project.name}
              description={description}
              imageUrls={imageUrls}
              href={`/du-an/${project.slug}`}
            />
          </div>
        );
      })}
    </div>
  );
}

function FindProject() {
  const loaderData = useLoaderData<typeof loader>();
  const { pageNumber, pageSize } = loaderData.page;
  const [searchParams, setSearchParams] = useSearchParams();
  const { province } = useDataDropdown();
  const provinceLabel =
    province.find((item:any) => item.value === loaderData.params.provinceId)?.label ||
    "";
  const hasProvince = Boolean(loaderData.params.provinceId && provinceLabel);
  const searchKeyword = loaderData.params.search?.trim();
  const hasSearchKeyword = Boolean(searchKeyword);

  const handleSetSearchParams = (next: Record<string, string | number>) => {
    const current = Object.fromEntries(searchParams);
    const merged = {
      ...current,
      ...Object.entries(next).reduce((acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      }, {} as Record<string, string>),
    };

    Object.keys(merged).forEach((key) => {
      if (
        merged[key] === "" ||
        merged[key] === "undefined" ||
        merged[key] === "null"
      ) {
        delete merged[key];
      }
    });
    setSearchParams(merged);
  };

  return (
    <div>
      <Hero image="/image/product.webp" title="Dự án" />
      <SectionChild>
        <FormSearchProduct params={loaderData.params} page={loaderData.page} />
        <BreadcrumbPageCus />
        <div className="pt-2 pb-6">
          <div className="text-xl font-semibold text-primary">
            Có {loaderData.dataProject.totalCount} kết quả thuộc Kland gợi ý cho bạn.
          </div>
          {(hasProvince || hasSearchKeyword) && (
            <div className="mt-2 text-sm text-muted-foreground">
              {hasProvince ? `Khu vực: ${provinceLabel}` : null}
              {hasProvince && hasSearchKeyword ? " · " : null}
              {hasSearchKeyword ? `Địa điểm: ${searchKeyword}` : null}
            </div>
          )}
        </div>
        <ListProject projects={loaderData.dataProject.data} />
        <Pagination
          currentPage={loaderData.dataProject.currentPage || pageNumber}
          totalPages={loaderData.dataProject.totalPages || 1}
          pageSize={loaderData.dataProject.pageSize || pageSize}
          totalItems={loaderData.dataProject.totalCount}
          onPageChange={(page) => handleSetSearchParams({ pageNumber: page })}
          onPageSizeChange={(size) =>
            handleSetSearchParams({ pageSize: size, pageNumber: 1 })
          }
        />
      </SectionChild>
      <SectionChild className="pb-[60px]">
        <FormContact />
      </SectionChild>
      <div className="pb-[100px]">
        <ContentPage content={loaderData.contentPage.content} />
      </div>
    </div>
  );
}

export default FindProject;
